<template>
  <div class="kv-ve__group">
    <div class="form-group">
      <label style="margin-bottom: -18px; display:block">{{ $_("visualEditor-emailEditor-emailDesign-background") }}</label>
      <VeBackgroundPicker
        v-model="bodyBackgroundProxy"
        label=""
        :color-label="$_('visualEditor-emailEditor-backgroundPicker-color')"
        :image-label="$_('visualEditor-emailEditor-backgroundPicker-image')"
        :choose-image-label="$_('visualEditor-emailEditor-backgroundPicker-choose')"
        :remove-image-label="$_('visualEditor-emailEditor-backgroundPicker-remove')"
        :empty-image-label="$_('visualEditor-emailEditor-backgroundPicker-empty')"
      />
    </div>

    <div class="form-group" style="margin-top: 36px; margin-bottom: 18px; display:block">
      <label style="margin-bottom: -18px; display:block">{{ $_("visualEditor-emailEditor-emailDesign-body") }}</label>
      <VeBackgroundPicker
        v-model="bodyContentBackgroundProxy"
        label=""
        :color-label="$_('visualEditor-emailEditor-backgroundPicker-color')"
        :image-label="$_('visualEditor-emailEditor-backgroundPicker-image')"
        :choose-image-label="$_('visualEditor-emailEditor-backgroundPicker-choose')"
        :remove-image-label="$_('visualEditor-emailEditor-backgroundPicker-remove')"
        :empty-image-label="$_('visualEditor-emailEditor-backgroundPicker-empty')"
      />
    </div>

    <div class="form-group">
      <VeWidthSlider
        v-model="emailWidthProxy"
        :label="$_('visualEditor-emailEditor-emailDesign-emailWidthLabel')"
        :min="320"
        :max="1000"
        :step="10"
        unit="px"
        :showHeader="true"
        :editableValue="true"
      />
    </div>

    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-emailDesign-mobilePaddingPx") }}</label>
      <input class="form-control" type="number" v-model.number="doc.body.mobilePadding" min="0" max="40" />
    </div>

    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-emailDesign-fontFamily") }}</label>
      <select class="form-control" v-model="doc.body.fontFamily">
        <option value="Helvetica, Arial, sans-serif">
          {{ $_("visualEditor-emailEditor-emailDesign-fontHelveticaArial") }}
        </option>
        <option value="Arial, Helvetica, sans-serif">
          {{ $_("visualEditor-emailEditor-emailDesign-fontArial") }}
        </option>
        <option value="Verdana, Geneva, sans-serif">
          {{ $_("visualEditor-emailEditor-emailDesign-fontVerdana") }}
        </option>
        <option value="Tahoma, Geneva, sans-serif">
          {{ $_("visualEditor-emailEditor-emailDesign-fontTahoma") }}
        </option>
        <option value="'Trebuchet MS', Helvetica, sans-serif">
          {{ $_("visualEditor-emailEditor-emailDesign-fontTrebuchetMs") }}
        </option>
        <option value="Georgia, serif">
          {{ $_("visualEditor-emailEditor-emailDesign-fontGeorgia") }}
        </option>
        <option value="'Times New Roman', Times, serif">
          {{ $_("visualEditor-emailEditor-emailDesign-fontTimesNewRoman") }}
        </option>
        <option value="'Courier New', Courier, monospace">
          {{ $_("visualEditor-emailEditor-emailDesign-fontCourierNew") }}
        </option>
      </select>
    </div>

    <div class="form-row">
      <div class="form-group col-6">
        <label>{{ $_("visualEditor-emailEditor-emailDesign-text") }}</label>
        <VeColorPicker v-model="doc.body.textColor" />
      </div>
      <div class="form-group col-6">
        <label>{{ $_("visualEditor-emailEditor-emailDesign-links") }}</label>
        <VeColorPicker v-model="doc.body.linkColor" />
      </div>
    </div>

    <div class="form-row">
      <div class="form-group col-6">
        <label>{{ $_("visualEditor-emailEditor-emailDesign-buttonBackground") }}</label>
        <VeColorPicker v-model="doc.body.buttonBackgroundColor" />
      </div>
      <div class="form-group col-6">
        <label>{{ $_("visualEditor-emailEditor-emailDesign-buttonText") }}</label>
        <VeColorPicker v-model="doc.body.buttonTextColor" />
      </div>
    </div>
  </div>
</template>

<script>
import VeColorPicker from "../components/VeColorPicker.vue";
import VeWidthSlider from "../components/VeWidthSlider.vue";
import VeBackgroundPicker from "../components/VeBackgroundPicker.vue";

export default {
  name: "VeEmailDesignSettings",
  components: { VeColorPicker, VeWidthSlider, VeBackgroundPicker },
  props: {
    doc: { type: Object, required: true },
    borderRadiusOptions: { type: Array, required: true },
  },
  computed: {
    emailWidthProxy: {
      get() {
        const body = this.doc?.body || {};
        const raw = body.emailWidth ?? body.width ?? 600;
        const n = Number(raw);
        if (!Number.isFinite(n)) return 600;
        return Math.min(1000, Math.max(320, Math.round(n)));
      },
      set(v) {
        const n = Number(v);
        const next = Number.isFinite(n) ? Math.min(1000, Math.max(320, Math.round(n))) : 600;
        if (!this.doc.body) this.$set(this.doc, "body", {});
        // Keep back-compat: renderer historically uses body.width
        this.$set(this.doc.body, "emailWidth", next);
        this.$set(this.doc.body, "width", next);
      },
    },
    bodyBackgroundProxy: {
      get() {
        const body = this.doc?.body || {};
        const mode = String(body.backgroundMode || "").toLowerCase() === "image" ? "image" : "color";
        const color = body.backgroundColor || "#ffffff";
        const imageUrl = body.backgroundImageUrl || null;
        const inferredMode = imageUrl ? "image" : mode;
        return {
          mode: inferredMode,
          color,
          imageUrl,
          sizeMode: body.backgroundSizeMode || "cover",
          sizeUnit: body.backgroundSizeUnit || "pct",
          sizePx: body.backgroundSizePx,
          sizePct: body.backgroundSizePct,
          repeat: body.backgroundRepeat || "no-repeat",
          position: body.backgroundPosition || "center center",
        };
      },
      set(next) {
        const v = next || {};
        const mode = String(v.mode || "color").toLowerCase() === "image" ? "image" : "color";
        const color = typeof v.color === "string" && v.color ? v.color : "#ffffff";
        const imageUrl = typeof v.imageUrl === "string" && v.imageUrl ? v.imageUrl : null;
        if (!this.doc.body) this.$set(this.doc, "body", {});
        this.$set(this.doc.body, "backgroundMode", mode);
        this.$set(this.doc.body, "backgroundColor", color === "#ffffff" ? null : color);
        this.$set(this.doc.body, "backgroundImageUrl", imageUrl);

        // Extra image controls (size/repeat/position)
        const sizeModeRaw = String(v.sizeMode || "").toLowerCase();
        const sizeMode = sizeModeRaw === "contain" || sizeModeRaw === "custom" ? sizeModeRaw : "cover";
        const sizeUnitRaw = String(v.sizeUnit || "").toLowerCase();
        const sizeUnit = sizeUnitRaw === "px" ? "px" : "pct";
        const px = Number(v.sizePx);
        const sizePx = Number.isFinite(px) ? Math.min(2000, Math.max(100, Math.round(px))) : 600;
        const pct = Number(v.sizePct);
        const sizePct = Number.isFinite(pct) ? Math.min(100, Math.max(0, Math.round(pct))) : 100;
        const repeatRaw = String(v.repeat || "").toLowerCase();
        const repeat =
          repeatRaw === "repeat" || repeatRaw === "repeat-x" || repeatRaw === "repeat-y"
            ? repeatRaw
            : "no-repeat";
        const position = String(v.position || "center center");

        this.$set(this.doc.body, "backgroundSizeMode", sizeMode);
        this.$set(this.doc.body, "backgroundSizeUnit", sizeUnit);
        this.$set(this.doc.body, "backgroundSizePx", sizePx);
        this.$set(this.doc.body, "backgroundSizePct", sizePct);
        this.$set(this.doc.body, "backgroundRepeat", repeat);
        this.$set(this.doc.body, "backgroundPosition", position);
      },
    },
    bodyContentBackgroundProxy: {
      get() {
        const body = this.doc?.body || {};
        const mode = String(body.contentBackgroundMode || "").toLowerCase() === "image" ? "image" : "color";
        const color = body.contentBackgroundColor || "#ffffff";
        const imageUrl = body.contentBackgroundImageUrl || null;
        const inferredMode = imageUrl ? "image" : mode;
        return {
          mode: inferredMode,
          color,
          imageUrl,
          sizeMode: body.contentBackgroundSizeMode || "cover",
          sizeUnit: body.contentBackgroundSizeUnit || "pct",
          sizePx: body.contentBackgroundSizePx,
          sizePct: body.contentBackgroundSizePct,
          repeat: body.contentBackgroundRepeat || "no-repeat",
          position: body.contentBackgroundPosition || "center center",
        };
      },
      set(next) {
        const v = next || {};
        const mode = String(v.mode || "color").toLowerCase() === "image" ? "image" : "color";
        const color = typeof v.color === "string" && v.color ? v.color : "#ffffff";
        const imageUrl = typeof v.imageUrl === "string" && v.imageUrl ? v.imageUrl : null;
        if (!this.doc.body) this.$set(this.doc, "body", {});
        this.$set(this.doc.body, "contentBackgroundMode", mode);
        this.$set(this.doc.body, "contentBackgroundColor", color === "#ffffff" ? null : color);
        this.$set(this.doc.body, "contentBackgroundImageUrl", imageUrl);

        // Extra image controls (size/repeat/position)
        const sizeModeRaw = String(v.sizeMode || "").toLowerCase();
        const sizeMode = sizeModeRaw === "contain" || sizeModeRaw === "custom" ? sizeModeRaw : "cover";
        const sizeUnitRaw = String(v.sizeUnit || "").toLowerCase();
        const sizeUnit = sizeUnitRaw === "px" ? "px" : "pct";
        const px = Number(v.sizePx);
        const sizePx = Number.isFinite(px) ? Math.min(2000, Math.max(100, Math.round(px))) : 600;
        const pct = Number(v.sizePct);
        const sizePct = Number.isFinite(pct) ? Math.min(100, Math.max(0, Math.round(pct))) : 100;
        const repeatRaw = String(v.repeat || "").toLowerCase();
        const repeat =
          repeatRaw === "repeat" || repeatRaw === "repeat-x" || repeatRaw === "repeat-y"
            ? repeatRaw
            : "no-repeat";
        const position = String(v.position || "center center");

        this.$set(this.doc.body, "contentBackgroundSizeMode", sizeMode);
        this.$set(this.doc.body, "contentBackgroundSizeUnit", sizeUnit);
        this.$set(this.doc.body, "contentBackgroundSizePx", sizePx);
        this.$set(this.doc.body, "contentBackgroundSizePct", sizePct);
        this.$set(this.doc.body, "contentBackgroundRepeat", repeat);
        this.$set(this.doc.body, "contentBackgroundPosition", position);
      },
    },
  },
  methods: {
    onBorderRadiusChange(value) {
      // Use Vue.set or direct assignment to ensure reactivity
      this.$set(this.doc.body, "borderRadius", value);
    },
  },
};
</script>


